import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import { FIELD_CREATION_TYPE, SEARCH_ACTION_GENERATE_FORM } from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  DocsSubmittedFormData,
  FormMetadata,
} from "core/types";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { SourceInformation } from "../../useDocsForm";
import { FORM_NAME_TO_FILE_TYPE, MigratedForms } from "../../utils";

export const useGetDischargeLetterOnSubmit = <T extends DocsFormName>({
  auction,
  formData: previousFormData,
}: {
  auction: Auction;
  formData: DocsFormDataMapped<T> | null | undefined;
}) => {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: { auctionId: auction.id },
      },
    ],
  });

  const onSubmit = useCallback(
    async (
      formData: DocsFormDataMapped<typeof formName>,
      formName: MigratedForms,
      sourceInformation: SourceInformation<typeof formName>,
      docsFillFormResult: DocsFormStatusResponse | null,
    ) => {
      const sourceFields: FormMetadata<typeof formName>["source_fields"] =
        Object.entries(formData).reduce(
          (acc, [key, value]) => {
            const inferredValue = docsFillFormResult?.form[key]?.value;
            const sourceField = sourceInformation.getSourceFields(
              key as keyof DocsFormDataMapped<typeof formName>,
            );
            const sourceDocuments = sourceField?.source_documents ?? [];
            const previousFieldValue =
              previousFormData?.[key as keyof DocsFormDataMapped<T>];

            return {
              ...acc,
              [key]: {
                creation_type:
                  inferredValue != null && value != null
                    ? FIELD_CREATION_TYPE.INFERENCE
                    : sourceField?.creation_type ?? FIELD_CREATION_TYPE.USER,
                manually_edited:
                  // if theres an inferred value, field is edited if the value submitted is different from the inferred value
                  inferredValue != null
                    ? value !== inferredValue
                    : // is the value wasnt inferred, and there was a previous value, check if the prvious value is different from the submitted value
                    previousFieldValue != null
                    ? previousFieldValue !== value ||
                      sourceField?.manually_edited
                    : // if the value wasnt inferred, and there was no previous value,
                      false,
                source_documents: sourceDocuments,
              },
            };
          },
          {} as FormMetadata<typeof formName>["source_fields"],
        );

      const submittedFormData: DocsSubmittedFormData<typeof formName> = {
        values: formData,
      };

      const metadata: FormMetadata<typeof formName> = {
        source_fields: sourceFields,
      };

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(
              JSON.stringify(submittedFormData),
            ),
            metadata,
          },
        ],
      } as Auction;

      await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
        onCompleted: () => {
          resetProgress();
        },
        onError: (err) => {
          console.error(`error saving form - ${getErrorMessage(err)}`);
        },
      });
    },
    [
      convertEncryptedFieldOut,
      generateForm,
      auction,
      previousFormData,
      encryptionContext,
    ],
  );

  return { onSubmit, onSubmitProgress: generateFormProgress };
};
